/**
 * Cache Performance Service
 * 
 * Comprehensive performance testing and monitoring for cache hit rates,
 * efficiency metrics, and optimization analysis.
 * 
 * Features:
 * - Real-time cache hit rate monitoring
 * - Memory usage analysis and optimization
 * - API call frequency tracking
 * - Cache efficiency reporting
 * - Performance benchmarking
 * - Automated performance testing
 */

import { QueryClient } from '@tanstack/react-query';
import { debugError, debugGroup, debugLog } from '../utils/debugLog';
import { DataType } from './cachingStrategyService';
import { CacheCoordinationService, CacheCoordinationMetrics } from './cacheCoordinationService';

// Performance metrics interfaces
export interface CacheHitRateMetrics {
  dataType: DataType;
  totalQueries: number;
  cacheHits: number;
  cacheMisses: number;
  hitRate: number;
  missRate: number;
  averageResponseTime: number;
  memoryUsage: number;
  lastUpdated: Date;
}

export interface APICallFrequencyMetrics {
  dataType: DataType;
  totalCalls: number;
  callsPerMinute: number;
  callsPerHour: number;
  duplicateCalls: number;
  duplicateRate: number;
  lastCall: Date | null;
  callHistory: APICallRecord[];
}

export interface APICallRecord {
  timestamp: Date;
  queryKey: string;
  duration: number;
  fromCache: boolean;
  dataSize: number;
}

export interface MemoryUsageMetrics {
  totalCacheSize: number;
  cacheEntries: number;
  averageEntrySize: number;
  largestEntry: {
    queryKey: string;
    size: number;
  };
  memoryEfficiency: number;
  gcCollections: number;
}

export interface CacheEfficiencyReport {
  timestamp: Date;
  overallHitRate: number;
  dataTypeMetrics: CacheHitRateMetrics[];
  apiCallMetrics: APICallFrequencyMetrics[];
  memoryMetrics: MemoryUsageMetrics;
  coordinationMetrics: CacheCoordinationMetrics;
  recommendations: PerformanceRecommendation[];
  performanceScore: number;
}

export interface PerformanceRecommendation {
  type: 'optimization' | 'warning' | 'info';
  category: 'cache-strategy' | 'memory' | 'api-calls' | 'coordination';
  message: string;
  impact: 'high' | 'medium' | 'low';
  action?: string;
}

export interface PerformanceBenchmark {
  name: string;
  description: string;
  target: number;
  current: number;
  status: 'excellent' | 'good' | 'needs-improvement' | 'poor';
  trend: 'improving' | 'stable' | 'declining';
}

/**
 * Cache Performance Service Class
 */
export class CachePerformanceService {
  private queryClient: QueryClient;
  private cacheCoordination: CacheCoordinationService;
  private performanceHistory: CacheEfficiencyReport[] = [];
  private apiCallHistory: Map<string, APICallRecord[]> = new Map();
  private performanceListeners: Set<(report: CacheEfficiencyReport) => void> = new Set();
  private monitoringInterval: NodeJS.Timeout | null = null;
  private startTime: Date = new Date();

  constructor(queryClient: QueryClient, cacheCoordination: CacheCoordinationService) {
    this.queryClient = queryClient;
    this.cacheCoordination = cacheCoordination;
    this.setupPerformanceMonitoring();
  }

  /**
   * Setup continuous performance monitoring
   */
  private setupPerformanceMonitoring(): void {
    // Monitor performance every 30 seconds
    this.monitoringInterval = setInterval(() => {
      this.generatePerformanceReport();
    }, 30000);

    debugLog('🔍 Cache performance monitoring started');
  }

  /**
   * Record API call for performance tracking
   */
  public recordAPICall(
    queryKey: string,
    duration: number,
    fromCache: boolean,
    dataSize: number = 0
  ): void {
    const record: APICallRecord = {
      timestamp: new Date(),
      queryKey,
      duration,
      fromCache,
      dataSize,
    };

    // Store in call history
    const history = this.apiCallHistory.get(queryKey) || [];
    history.push(record);
    
    // Keep only last 100 calls per query key
    if (history.length > 100) {
      history.shift();
    }
    
    this.apiCallHistory.set(queryKey, history);

    // Update coordination service metrics
    if (fromCache) {
      if (queryKey.includes('shows') && !queryKey.includes('show","')) {
        this.cacheCoordination.recordCacheHit('bulk');
      } else if (queryKey.includes('show","')) {
        this.cacheCoordination.recordCacheHit('individual');
      }
    } else {
      this.cacheCoordination.recordCacheMiss();
    }
  }

  /**
   * Generate comprehensive cache hit rate metrics
   */
  public getCacheHitRateMetrics(): CacheHitRateMetrics[] {
    const cache = this.queryClient.getQueryCache();
    const queries = cache.getAll();
    const dataTypeMetrics: Map<DataType, CacheHitRateMetrics> = new Map();

    // Initialize metrics for all data types
    Object.values(DataType).forEach(dataType => {
      dataTypeMetrics.set(dataType, {
        dataType,
        totalQueries: 0,
        cacheHits: 0,
        cacheMisses: 0,
        hitRate: 0,
        missRate: 0,
        averageResponseTime: 0,
        memoryUsage: 0,
        lastUpdated: new Date(),
      });
    });

    // Analyze queries by data type
    queries.forEach(query => {
      const dataType = this.getDataTypeFromQueryKey(query.queryKey);
      if (!dataType) return;

      const metrics = dataTypeMetrics.get(dataType)!;
      metrics.totalQueries++;

      // Analyze query state to determine cache behavior
      const queryState = query.state;
      const hasData = queryState.data !== undefined;
      const isStale = query.isStale();
      const isFetching = queryState.fetchStatus === 'fetching';
      const lastFetch = queryState.dataUpdatedAt;
      
      // Determine cache hits vs misses based on query behavior
      if (hasData && !isFetching) {
        // Query has data and is not currently fetching = cache hit
        metrics.cacheHits++;
      } else if (isFetching && !hasData) {
        // Query is fetching and has no data = initial cache miss
        metrics.cacheMisses++;
      } else if (isFetching && hasData && isStale) {
        // Query is refetching stale data = background refetch (count as cache hit since data is served)
        metrics.cacheHits++;
      } else if (hasData && isStale && !isFetching) {
        // Query has stale data but not refetching = cache hit with stale data
        metrics.cacheHits++;
      }

      // Calculate average response time based on data age
      if (lastFetch && hasData) {
        const dataAge = Date.now() - lastFetch;
        // Use data age as a proxy for response time (fresh data = fast response)
        metrics.averageResponseTime = dataAge < 1000 ? 50 : dataAge < 5000 ? 200 : 500;
      }

      // Estimate memory usage
      const dataSize = hasData ? JSON.stringify(queryState.data).length : 0;
      metrics.memoryUsage += dataSize;
    });

    // Calculate hit/miss rates with NaN protection
    dataTypeMetrics.forEach(metrics => {
      const total = metrics.cacheHits + metrics.cacheMisses;
      if (total > 0 && isFinite(total)) {
        metrics.hitRate = metrics.cacheHits / total;
        metrics.missRate = metrics.cacheMisses / total;
      } else {
        metrics.hitRate = 0;
        metrics.missRate = 0;
      }
      
      // Ensure values are valid numbers
      if (isNaN(metrics.hitRate) || !isFinite(metrics.hitRate)) {
        metrics.hitRate = 0;
      }
      if (isNaN(metrics.missRate) || !isFinite(metrics.missRate)) {
        metrics.missRate = 0;
      }
    });

    return Array.from(dataTypeMetrics.values());
  }

  /**
   * Generate API call frequency metrics
   */
  public getAPICallFrequencyMetrics(): APICallFrequencyMetrics[] {
    const dataTypeMetrics: Map<DataType, APICallFrequencyMetrics> = new Map();
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    const oneMinuteAgo = new Date(now.getTime() - 60 * 1000);

    // Initialize metrics for all data types
    Object.values(DataType).forEach(dataType => {
      dataTypeMetrics.set(dataType, {
        dataType,
        totalCalls: 0,
        callsPerMinute: 0,
        callsPerHour: 0,
        duplicateCalls: 0,
        duplicateRate: 0,
        lastCall: null,
        callHistory: [],
      });
    });

    // Analyze API call history
    this.apiCallHistory.forEach((history, queryKey) => {
      const dataType = this.getDataTypeFromQueryKey(JSON.parse(queryKey));
      if (!dataType) return;

      const metrics = dataTypeMetrics.get(dataType)!;
      
      // Filter calls by time period
      const hourCalls = history.filter(call => call.timestamp >= oneHourAgo);
      const minuteCalls = history.filter(call => call.timestamp >= oneMinuteAgo);
      const allCalls = history.filter(call => !call.fromCache); // Only actual API calls

      metrics.totalCalls += allCalls.length;
      metrics.callsPerHour += hourCalls.filter(call => !call.fromCache).length;
      metrics.callsPerMinute += minuteCalls.filter(call => !call.fromCache).length;

      // Find most recent call
      if (history.length > 0) {
        const lastCall = history[history.length - 1];
        if (!metrics.lastCall || lastCall.timestamp > metrics.lastCall) {
          metrics.lastCall = lastCall.timestamp;
        }
      }

      // Detect duplicate calls (same query within short time)
      const duplicates = this.detectDuplicateCalls(history);
      metrics.duplicateCalls += duplicates;
      metrics.callHistory.push(...history.slice(-10)); // Keep recent calls
    });

    // Calculate duplicate rates
    dataTypeMetrics.forEach(metrics => {
      if (metrics.totalCalls > 0) {
        metrics.duplicateRate = metrics.duplicateCalls / metrics.totalCalls;
      }
    });

    return Array.from(dataTypeMetrics.values());
  }

  /**
   * Generate memory usage metrics
   */
  public getMemoryUsageMetrics(): MemoryUsageMetrics {
    const cache = this.queryClient.getQueryCache();
    const queries = cache.getAll();
    
    let totalSize = 0;
    let largestEntry = { queryKey: '', size: 0 };
    let gcCollections = 0;

    queries.forEach(query => {
      const dataSize = JSON.stringify(query.state.data || {}).length;
      totalSize += dataSize;

      if (dataSize > largestEntry.size) {
        largestEntry = {
          queryKey: JSON.stringify(query.queryKey),
          size: dataSize,
        };
      }

      // Count queries that have been garbage collected
      if (query.state.dataUpdatedAt && 
          Date.now() - query.state.dataUpdatedAt > 60 * 60 * 1000) {
        gcCollections++;
      }
    });

    const averageEntrySize = queries.length > 0 ? totalSize / queries.length : 0;
    
    // Calculate memory efficiency with NaN protection
    let memoryEfficiency = 1; // Default to perfect efficiency
    if (totalSize > 0 && queries.length > 0) {
      memoryEfficiency = (queries.length * 1000) / totalSize;
      // Clamp efficiency between 0 and 1
      memoryEfficiency = Math.max(0, Math.min(1, memoryEfficiency));
    }
    
    // Ensure efficiency is a valid number
    if (isNaN(memoryEfficiency) || !isFinite(memoryEfficiency)) {
      memoryEfficiency = 0;
    }

    return {
      totalCacheSize: totalSize,
      cacheEntries: queries.length,
      averageEntrySize,
      largestEntry,
      memoryEfficiency,
      gcCollections,
    };
  }

  /**
   * Generate comprehensive performance report
   */
  public generatePerformanceReport(): CacheEfficiencyReport {
    const hitRateMetrics = this.getCacheHitRateMetrics();
    const apiCallMetrics = this.getAPICallFrequencyMetrics();
    const memoryMetrics = this.getMemoryUsageMetrics();
    const coordinationMetrics = this.cacheCoordination.getMetrics();

    // Calculate overall hit rate with NaN protection
    const totalHits = hitRateMetrics.reduce((sum, m) => sum + (isNaN(m.cacheHits) ? 0 : m.cacheHits), 0);
    const totalMisses = hitRateMetrics.reduce((sum, m) => sum + (isNaN(m.cacheMisses) ? 0 : m.cacheMisses), 0);
    const totalRequests = totalHits + totalMisses;
    const overallHitRate = totalRequests > 0 && isFinite(totalRequests) ? totalHits / totalRequests : 0;

    // Generate recommendations
    const recommendations = this.generateRecommendations(
      hitRateMetrics,
      apiCallMetrics,
      memoryMetrics,
      coordinationMetrics
    );

    // Calculate performance score
    const performanceScore = this.calculatePerformanceScore(
      overallHitRate,
      apiCallMetrics,
      memoryMetrics,
      coordinationMetrics
    );

    const report: CacheEfficiencyReport = {
      timestamp: new Date(),
      overallHitRate,
      dataTypeMetrics: hitRateMetrics,
      apiCallMetrics,
      memoryMetrics,
      coordinationMetrics,
      recommendations,
      performanceScore,
    };

    // Store in history
    this.performanceHistory.push(report);
    if (this.performanceHistory.length > 100) {
      this.performanceHistory.shift();
    }

    // Notify listeners
    this.performanceListeners.forEach(listener => {
      try {
        listener(report);
      } catch (error) {
        debugError('Error in performance listener:', error);
      }
    });

    debugGroup('📊 Performance report generated', () => {
      debugLog({
        overallHitRate: `${(overallHitRate * 100).toFixed(1)}%`,
        performanceScore: `${performanceScore}/100`,
        totalQueries: hitRateMetrics.reduce((sum, m) => sum + m.totalQueries, 0),
        memoryUsage: `${(memoryMetrics.totalCacheSize / 1024).toFixed(1)}KB`,
      });
    });

    return report;
  }

  /**
   * Get performance benchmarks
   */
  public getPerformanceBenchmarks(): PerformanceBenchmark[] {
    const latestReport = this.performanceHistory[this.performanceHistory.length - 1];
    const previousReport = this.performanceHistory[this.performanceHistory.length - 2];

    if (!latestReport) {
      return [];
    }

    const benchmarks: PerformanceBenchmark[] = [
      {
        name: 'Overall Cache Hit Rate',
        description: 'Percentage of queries served from cache',
        target: 0.85, // 85% target
        current: latestReport.overallHitRate,
        status: this.getBenchmarkStatus(latestReport.overallHitRate, 0.85),
        trend: this.getTrend(latestReport.overallHitRate, previousReport?.overallHitRate),
      },
      {
        name: 'API Call Reduction',
        description: 'Reduction in API calls due to caching',
        target: 0.5, // 50% reduction target
        current: this.calculateAPICallReduction(latestReport.apiCallMetrics),
        status: this.getBenchmarkStatus(this.calculateAPICallReduction(latestReport.apiCallMetrics), 0.5),
        trend: this.getTrend(
          this.calculateAPICallReduction(latestReport.apiCallMetrics),
          previousReport ? this.calculateAPICallReduction(previousReport.apiCallMetrics) : undefined
        ),
      },
      {
        name: 'Memory Efficiency',
        description: 'Cache memory usage efficiency',
        target: 0.8, // 80% efficiency target
        current: latestReport.memoryMetrics.memoryEfficiency,
        status: this.getBenchmarkStatus(latestReport.memoryMetrics.memoryEfficiency, 0.8),
        trend: this.getTrend(
          latestReport.memoryMetrics.memoryEfficiency,
          previousReport?.memoryMetrics.memoryEfficiency
        ),
      },
      {
        name: 'Cache Coordination Events',
        description: 'Number of successful cache coordination events',
        target: 10, // 10 events target
        current: latestReport.coordinationMetrics.coordinationEvents,
        status: this.getBenchmarkStatus(latestReport.coordinationMetrics.coordinationEvents, 10),
        trend: this.getTrend(
          latestReport.coordinationMetrics.coordinationEvents,
          previousReport?.coordinationMetrics.coordinationEvents
        ),
      },
    ];

    return benchmarks;
  }

  /**
   * Run automated performance tests
   */
  public async runPerformanceTests(): Promise<{
    passed: number;
    failed: number;
    results: PerformanceTestResult[];
  }> {
    debugLog('🧪 Running automated cache performance tests...');

    const tests: PerformanceTest[] = [
      {
        name: 'Cache Hit Rate Test',
        description: 'Verify cache hit rate meets minimum threshold',
        test: async () => {
          const report = this.generatePerformanceReport();
          return {
            passed: report.overallHitRate >= 0.75, // 75% minimum
            value: report.overallHitRate,
            expected: 0.75,
            message: `Cache hit rate: ${(report.overallHitRate * 100).toFixed(1)}%`,
          };
        },
      },
      {
        name: 'Memory Usage Test',
        description: 'Verify memory usage is within acceptable limits',
        test: async () => {
          const metrics = this.getMemoryUsageMetrics();
          const maxMemory = 5 * 1024 * 1024; // 5MB limit
          return {
            passed: metrics.totalCacheSize <= maxMemory,
            value: metrics.totalCacheSize,
            expected: maxMemory,
            message: `Memory usage: ${(metrics.totalCacheSize / 1024).toFixed(1)}KB`,
          };
        },
      },
      {
        name: 'API Call Frequency Test',
        description: 'Verify API call frequency is optimized',
        test: async () => {
          const metrics = this.getAPICallFrequencyMetrics();
          const totalCallsPerMinute = metrics.reduce((sum, m) => sum + m.callsPerMinute, 0);
          const maxCallsPerMinute = 10; // 10 calls per minute limit
          return {
            passed: totalCallsPerMinute <= maxCallsPerMinute,
            value: totalCallsPerMinute,
            expected: maxCallsPerMinute,
            message: `API calls per minute: ${totalCallsPerMinute}`,
          };
        },
      },
      {
        name: 'Cache Coordination Test',
        description: 'Verify cache coordination is working',
        test: async () => {
          const metrics = this.cacheCoordination.getMetrics();
          return {
            passed: metrics.coordinationEvents > 0,
            value: metrics.coordinationEvents,
            expected: 1,
            message: `Coordination events: ${metrics.coordinationEvents}`,
          };
        },
      },
      {
        name: 'Duplicate API Calls Test',
        description: 'Verify minimal duplicate API calls',
        test: async () => {
          const metrics = this.getAPICallFrequencyMetrics();
          const avgDuplicateRate = metrics.reduce((sum, m) => sum + m.duplicateRate, 0) / metrics.length;
          const maxDuplicateRate = 0.1; // 10% maximum
          return {
            passed: avgDuplicateRate <= maxDuplicateRate,
            value: avgDuplicateRate,
            expected: maxDuplicateRate,
            message: `Average duplicate rate: ${(avgDuplicateRate * 100).toFixed(1)}%`,
          };
        },
      },
    ];

    const results: PerformanceTestResult[] = [];
    let passed = 0;
    let failed = 0;

    for (const test of tests) {
      try {
        const result = await test.test();
        results.push({
          name: test.name,
          description: test.description,
          ...result,
        });

        if (result.passed) {
          passed++;
          debugLog(`✅ ${test.name}: ${result.message}`);
        } else {
          failed++;
          debugLog(`❌ ${test.name}: ${result.message}`);
        }
      } catch (error) {
        failed++;
        results.push({
          name: test.name,
          description: test.description,
          passed: false,
          value: 0,
          expected: 0,
          message: `Test failed with error: ${error}`,
        });
        debugLog(`❌ ${test.name}: Test failed with error`);
      }
    }

    debugLog(`🧪 Performance tests completed: ${passed} passed, ${failed} failed`);

    return { passed, failed, results };
  }

  /**
   * Add performance listener
   */
  public addPerformanceListener(listener: (report: CacheEfficiencyReport) => void): void {
    this.performanceListeners.add(listener);
  }

  /**
   * Remove performance listener
   */
  public removePerformanceListener(listener: (report: CacheEfficiencyReport) => void): void {
    this.performanceListeners.delete(listener);
  }

  /**
   * Get performance history
   */
  public getPerformanceHistory(): CacheEfficiencyReport[] {
    return [...this.performanceHistory];
  }

  /**
   * Reset performance metrics
   */
  public resetMetrics(): void {
    this.performanceHistory.length = 0;
    this.apiCallHistory.clear();
    this.cacheCoordination.resetMetrics();
    this.startTime = new Date();
    debugLog('📊 Performance metrics reset');
  }

  /**
   * Destroy performance service
   */
  public destroy(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    this.performanceListeners.clear();
    debugLog('🔍 Cache performance monitoring stopped');
  }

  // Helper methods
  private getDataTypeFromQueryKey(queryKey: readonly unknown[]): DataType | null {
    const keyStr = JSON.stringify(queryKey);
    
    if (keyStr.includes('liveShow')) return DataType.LIVE_DATA;
    if (keyStr.includes('schedule')) return DataType.SCHEDULE_DATA;
    if (keyStr.includes('shows') && !keyStr.includes('show","')) return DataType.PAST_SHOWS_DATA;
    if (keyStr.includes('show","')) return DataType.INDIVIDUAL_SHOW_DATA;
    if (keyStr.includes('presenters')) return DataType.PRESENTERS_DATA;
    if (keyStr.includes('shopify-products')) return DataType.PRODUCT_CATALOG;
    if (keyStr.includes('shopify-collections')) return DataType.COLLECTIONS;
    if (keyStr.includes('cart')) return DataType.CART_DATA;
    if (keyStr.includes('homepageData')) return DataType.HOMEPAGE_DATA;
    if (keyStr.includes('blog')) return DataType.BLOG_CONTENT;
    
    return null;
  }

  private detectDuplicateCalls(history: APICallRecord[]): number {
    let duplicates = 0;
    const timeWindow = 5000; // 5 seconds

    for (let i = 1; i < history.length; i++) {
      const current = history[i];
      const previous = history[i - 1];
      
      if (current.timestamp.getTime() - previous.timestamp.getTime() < timeWindow &&
          !current.fromCache && !previous.fromCache) {
        duplicates++;
      }
    }

    return duplicates;
  }

  private generateRecommendations(
    hitRateMetrics: CacheHitRateMetrics[],
    apiCallMetrics: APICallFrequencyMetrics[],
    memoryMetrics: MemoryUsageMetrics,
    coordinationMetrics: CacheCoordinationMetrics
  ): PerformanceRecommendation[] {
    const recommendations: PerformanceRecommendation[] = [];

    // Check hit rates
    hitRateMetrics.forEach(metrics => {
      if (metrics.hitRate < 0.75) {
        recommendations.push({
          type: 'warning',
          category: 'cache-strategy',
          message: `${metrics.dataType} has low cache hit rate (${(metrics.hitRate * 100).toFixed(1)}%)`,
          impact: 'high',
          action: 'Consider increasing stale time or implementing prefetching',
        });
      }
    });

    // Check API call frequency
    const highFrequencyTypes = apiCallMetrics.filter(m => m.callsPerMinute > 5);
    highFrequencyTypes.forEach(metrics => {
      recommendations.push({
        type: 'warning',
        category: 'api-calls',
        message: `${metrics.dataType} has high API call frequency (${metrics.callsPerMinute}/min)`,
        impact: 'medium',
        action: 'Consider implementing smart polling or increasing cache duration',
      });
    });

    // Check memory usage
    if (memoryMetrics.totalCacheSize > 3 * 1024 * 1024) { // 3MB
      recommendations.push({
        type: 'warning',
        category: 'memory',
        message: `High memory usage (${(memoryMetrics.totalCacheSize / 1024).toFixed(1)}KB)`,
        impact: 'medium',
        action: 'Consider implementing cache size limits or more aggressive garbage collection',
      });
    }

    // Check coordination
    if (coordinationMetrics.coordinationEvents === 0) {
      recommendations.push({
        type: 'info',
        category: 'coordination',
        message: 'No cache coordination events detected',
        impact: 'low',
        action: 'Verify cache coordination service is properly integrated',
      });
    }

    return recommendations;
  }

  private calculatePerformanceScore(
    overallHitRate: number,
    apiCallMetrics: APICallFrequencyMetrics[],
    memoryMetrics: MemoryUsageMetrics,
    coordinationMetrics: CacheCoordinationMetrics
  ): number {
    let score = 0;

    // Hit rate score (40% of total) - handle NaN and invalid values
    const hitRateScore = isNaN(overallHitRate) || !isFinite(overallHitRate) ? 0 : Math.min(overallHitRate / 0.85, 1) * 40;
    score += hitRateScore;

    // API efficiency score (30% of total) - handle NaN and invalid values
    const totalCallsPerMinute = apiCallMetrics.reduce((sum, m) => {
      const callsPerMinute = isNaN(m.callsPerMinute) || !isFinite(m.callsPerMinute) ? 0 : m.callsPerMinute;
      return sum + callsPerMinute;
    }, 0);
    const apiEfficiency = Math.max(0, 1 - totalCallsPerMinute / 20); // 20 calls/min = 0 score
    const apiScore = isNaN(apiEfficiency) || !isFinite(apiEfficiency) ? 0 : apiEfficiency * 30;
    score += apiScore;

    // Memory efficiency score (20% of total) - handle NaN and invalid values
    const memoryEfficiency = isNaN(memoryMetrics.memoryEfficiency) || !isFinite(memoryMetrics.memoryEfficiency) 
      ? 0 
      : Math.min(memoryMetrics.memoryEfficiency, 1);
    const memoryScore = memoryEfficiency * 20;
    score += memoryScore;

    // Coordination score (10% of total) - handle invalid values
    const coordinationEvents = isNaN(coordinationMetrics.coordinationEvents) || !isFinite(coordinationMetrics.coordinationEvents) 
      ? 0 
      : coordinationMetrics.coordinationEvents;
    const coordinationScore = coordinationEvents > 0 ? 1 : 0;
    score += coordinationScore * 10;

    // Ensure final score is valid
    const finalScore = isNaN(score) || !isFinite(score) ? 0 : Math.round(score);
    return Math.max(0, Math.min(100, finalScore)); // Clamp between 0-100
  }

  private calculateAPICallReduction(apiCallMetrics: APICallFrequencyMetrics[]): number {
    // Estimate API call reduction based on cache hits vs total potential calls
    const totalPotentialCalls = apiCallMetrics.reduce((sum, m) => 
      sum + m.totalCalls + m.callHistory.filter(c => c.fromCache).length, 0
    );
    const actualAPICalls = apiCallMetrics.reduce((sum, m) => sum + m.totalCalls, 0);
    
    return totalPotentialCalls > 0 ? 1 - (actualAPICalls / totalPotentialCalls) : 0;
  }

  private getBenchmarkStatus(current: number, target: number): 'excellent' | 'good' | 'needs-improvement' | 'poor' {
    const ratio = current / target;
    if (ratio >= 1.1) return 'excellent';
    if (ratio >= 1.0) return 'good';
    if (ratio >= 0.8) return 'needs-improvement';
    return 'poor';
  }

  private getTrend(current: number, previous?: number): 'improving' | 'stable' | 'declining' {
    if (previous === undefined) return 'stable';
    const change = (current - previous) / previous;
    if (change > 0.05) return 'improving';
    if (change < -0.05) return 'declining';
    return 'stable';
  }
}

// Supporting interfaces
interface PerformanceTest {
  name: string;
  description: string;
  test: () => Promise<{
    passed: boolean;
    value: number;
    expected: number;
    message: string;
  }>;
}

interface PerformanceTestResult {
  name: string;
  description: string;
  passed: boolean;
  value: number;
  expected: number;
  message: string;
}

export default CachePerformanceService;
